// Leaflets: the people delivering leaflets, the zones (areas of streets)
// they're given and the jobs (bundles of leaflets) they're doing. They use
// their own app at leaflets.teardrophydro.co.uk to see their zone and send in
// a screenshot of each round; everything is checked and paid from here. The
// open tab lives in the URL (?tab=check) so a reload keeps it.
import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/auth';
import { Button, PageHeader } from '../lib/ui';
import { formatGBP, todayYmd } from '../lib/trade';
import { useLeafletsData } from '../lib/useLeafletsData';
import { roundsToCheck, personMoney, PERSON_STATUS } from '../lib/leaflets';
import { useFlash, errorText } from '../components/TradeCommon';
import { byId } from '../components/LeafletsCommon';
import LeafletsMapTab from '../components/LeafletsMapTab';
import LeafletsReviewTab from '../components/LeafletsRounds';
import { JobsList, JobDetail, JobModal, LogRoundModal } from '../components/LeafletsJobs';
import LeafletsZonesList from '../components/LeafletsZonesList';
import LeafletsZoneEditor from '../components/LeafletsZoneEditor';
import { PeopleList, PersonDetail, PersonModal, PasswordModal } from '../components/LeafletsPeople';
import LeafletsPaymentsTab, { PayModal } from '../components/LeafletsPayments';
import LeafletsSheet from '../components/LeafletsSheet';
import LeafletsSettings from '../components/LeafletsSettings';
import '../components/Trade.css';
import '../components/Leaflets.css';

const TAB_KEYS = ['map', 'check', 'jobs', 'zones', 'people', 'payments'];

const SUBTITLES = {
  map:      'Who has which streets. Click a zone to give it out, print its route sheet or change it',
  check:    'Rounds sent in from the app with their screenshots. Approve them to pay, or send them back',
  jobs:     'Bundles of leaflets given out: who has them, where, and how far they\'ve got',
  zones:    'The areas of streets you give out, drawn on the map',
  people:   'Everyone delivering for you, with their logins for the app',
  payments: 'What\'s owed for approved rounds, and what\'s been paid',
};

export default function LeafletsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : 'map';
  const setTab = useCallback((t) => setParams(t === 'map' ? {} : { tab: t }, { replace: true }), [setParams]);

  const data = useLeafletsData();
  const { flash, banner } = useFlash();
  const today = todayYmd();
  const ready = data.status === 'ready';

  const [jobId, setJobId] = useState(null);       // job open on the Jobs tab
  const [personId, setPersonId] = useState(null); // person open on the People tab
  const [zoneEdit, setZoneEdit] = useState(null); // { zone } being edited ({ zone: null } = new)
  const [modal, setModal] = useState(null);       // { kind, ... }
  const editorDirty = useRef(false);

  const people = byId(data.distributors);
  const closeModal = () => setModal(null);

  const leaveEditor = () => {
    if (zoneEdit && editorDirty.current && !window.confirm('Leave the zone without saving your changes?')) return false;
    editorDirty.current = false;
    setZoneEdit(null);
    return true;
  };

  // Clicking the tab you're on takes you back to its list.
  const onTabChange = (t) => {
    if (!leaveEditor()) return;
    if (t === tab) {
      setJobId(null);
      setPersonId(null);
    }
    setTab(t);
  };

  const actions = {
    editZone: (zone) => {
      if (!leaveEditor()) return;
      setZoneEdit({ zone });
      setTab('zones');
    },
    assign: (start) => setModal({ kind: 'job', start }),
    editJob: (job) => setModal({ kind: 'job', start: { job } }),
    logRound: (job) => setModal({ kind: 'round', job }),
    print: ({ zone, job }) => setModal({ kind: 'sheet', zone, job, person: job ? people[job.distributor] : null, settings: data.settings }),
    openJob: (id) => { setJobId(id); setTab('jobs'); },
    openPerson: (id) => { setPersonId(id); setTab('people'); },
    openReview: () => setTab('check'),
    openPayments: () => setTab('payments'),
    editPerson: (person) => setModal({ kind: 'person', person }),
    resetPassword: (person) => setModal({ kind: 'password', person }),
    pay: (person) => setModal({ kind: 'pay', person }),
    setPersonStatus: async (person, status) => {
      try {
        const { data: res } = await api.patch(`/leaflets/admin/distributors/${person._id}`, { status });
        data.putPerson(res.distributor);
        flash(`${person.name} is now ${PERSON_STATUS[status].label.toLowerCase()}`);
      } catch (e) {
        flash(errorText(e), 'bad');
      }
    },
  };

  const toCheck = roundsToCheck(data.jobs).length;
  const owedPeople = data.distributors.filter(p => personMoney(p._id, data.jobs).owed > 0).length;
  const job = tab === 'jobs' && jobId ? data.jobs.find(j => j._id === jobId) : null;
  const person = tab === 'people' && personId ? people[personId] : null;
  const editing = tab === 'zones' && zoneEdit;

  let headerActions = null;
  if (ready && !editing) {
    const settingsBtn = <Button variant="muted" onClick={() => setModal({ kind: 'settings' })}>Settings</Button>;
    const newZone = <Button onClick={() => actions.editZone(null)}>+ New zone</Button>;
    const giveOut = <Button variant="primary" onClick={() => actions.assign({})}>Give out leaflets</Button>;
    headerActions = {
      map:      <>{settingsBtn}{newZone}{giveOut}</>,
      check:    settingsBtn,
      jobs:     !job && <>{settingsBtn}{giveOut}</>,
      zones:    <>{settingsBtn}{newZone}</>,
      people:   !person && <>{settingsBtn}<Button variant="primary" onClick={() => setModal({ kind: 'person', person: null })}>+ Add someone</Button></>,
      payments: settingsBtn,
    }[tab] || null;
  }

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Leaflets"
        subtitle={SUBTITLES[tab]}
        tabs={[
          { key: 'map', label: 'Map' },
          { key: 'check', label: 'To check', count: toCheck, title: toCheck ? `${toCheck} round${toCheck === 1 ? '' : 's'} waiting` : 'Rounds sent in from the app' },
          { key: 'jobs', label: 'Jobs' },
          { key: 'zones', label: 'Zones' },
          { key: 'people', label: 'People' },
          { key: 'payments', label: 'Payments', count: owedPeople, title: owedPeople ? `${owedPeople} to pay` : 'Payments' },
        ]}
        activeTab={tab}
        onTabChange={onTabChange}
        actions={headerActions}
      />

      {data.status === 'loading' && <div className="loader">Loading leaflets…</div>}
      {data.status === 'missing' && (
        <div className="ui-banner ui-banner--warn">
          The server doesn't know about leaflets yet. Deploy the latest garageFarmServer (it adds routes/leaflets.js), then reload this page.
        </div>
      )}
      {data.status === 'error' && (
        <div className="ui-banner ui-banner--bad trade-row">
          Couldn't load leaflets: {data.error}
          <Button size="sm" onClick={data.reload}>Try again</Button>
        </div>
      )}

      {ready && tab === 'map' && <LeafletsMapTab data={data} today={today} actions={actions} />}
      {ready && tab === 'check' && <LeafletsReviewTab data={data} putJob={data.putJob} flash={flash} />}
      {ready && tab === 'jobs' && (job
        ? <JobDetail job={job} data={data} actions={actions} putJob={data.putJob} dropJob={data.dropJob} flash={flash} onBack={() => setJobId(null)} />
        : <JobsList data={data} onOpen={setJobId} />)}
      {ready && tab === 'zones' && (editing ? (
        <LeafletsZoneEditor
          key={zoneEdit.zone?._id || 'new'}
          zone={zoneEdit.zone ? data.zones.find(z => z._id === zoneEdit.zone._id) || zoneEdit.zone : null}
          data={data}
          onDirty={(d) => { editorDirty.current = d; }}
          onClose={() => { editorDirty.current = false; setZoneEdit(null); }}
          onSaved={(zone, created, { stay } = {}) => {
            data.putZone(zone);
            if (stay) return flash('Ticks cleared');
            editorDirty.current = false;
            setZoneEdit(null);
            flash(created ? `${zone.name} saved. Give it out from the Map tab or the list.` : 'Saved');
          }}
          onDeleted={(id) => {
            data.dropZone(id);
            editorDirty.current = false;
            setZoneEdit(null);
            flash('Zone deleted', 'info');
          }}
        />
      ) : <LeafletsZonesList data={data} today={today} actions={actions} />)}
      {ready && tab === 'people' && (person
        ? <PersonDetail person={person} data={data} actions={actions} onBack={() => setPersonId(null)} />
        : <PeopleList data={data} onOpen={setPersonId} />)}
      {ready && tab === 'payments' && (
        <LeafletsPaymentsTab data={data} actions={actions} dropPayout={data.dropPayout} putJobs={data.putJobs} flash={flash} />
      )}

      {modal?.kind === 'job' && (
        <JobModal start={modal.start} data={data} today={today} onClose={closeModal} onSaved={(saved, created) => {
          data.putJob(saved);
          closeModal();
          if (created) {
            setJobId(saved._id);
            setTab('jobs');
            flash('Leaflets given out. Print the route sheet from the job if they want one.');
          } else {
            flash('Saved');
          }
        }} />
      )}
      {modal?.kind === 'round' && (
        <LogRoundModal job={modal.job} data={data} today={today} onClose={closeModal} onSaved={(saved) => {
          data.putJob(saved);
          closeModal();
          flash('Round logged and approved');
        }} />
      )}
      {modal?.kind === 'sheet' && (
        <LeafletsSheet zone={modal.zone} job={modal.job} person={modal.person} settings={modal.settings} onClose={closeModal} />
      )}
      {modal?.kind === 'person' && (
        <PersonModal person={modal.person} onClose={closeModal} onSaved={(saved, created) => {
          data.putPerson(saved);
          closeModal();
          flash(created ? `${saved.name} added` : 'Saved');
          if (created) {
            setPersonId(saved._id);
            setTab('people');
          }
        }} />
      )}
      {modal?.kind === 'password' && <PasswordModal person={modal.person} onClose={closeModal} onDone={data.putPerson} />}
      {modal?.kind === 'pay' && (
        <PayModal person={modal.person} data={data} today={today} onClose={closeModal} onPaid={({ payout, jobs }) => {
          data.putPayout(payout);
          data.putJobs(jobs);
          closeModal();
          flash(`${formatGBP(payout.amount)} to ${modal.person.name} marked paid`);
        }} />
      )}
      {modal?.kind === 'settings' && (
        <LeafletsSettings settings={data.settings} onClose={closeModal} onSaved={(s) => {
          data.setSettings(s);
          closeModal();
          flash('Settings saved');
        }} />
      )}
      {banner}
    </div>
  );
}
